import React, { useCallback, useEffect, useState } from 'react';
import { Collapse, Drawer, List, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import Fetch from 'jsx/common/util/_fetch';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { Components } from 'react-markdown/lib/ast-to-react';
import gfm from 'remark-gfm';
import classNames from 'classnames';

const { Title } = Typography;

const { Panel } = Collapse;

export const StyledNoInfoMessage = ({
  className,
  ...props
}: React.ComponentProps<'p'>) => {
  return (
    <p
      className={classNames('pt-[10px] pb-0 pr-0 pl-5', className)}
      {...props}
    />
  );
};

type QuestionDrawerProps = {
  isDrawerVisible: boolean;
  handleClose: () => void;
};

const components: Components = {
  h1: ({ children }) => <Title level={3}>{children}</Title>,
  h4: ({ children }) => <Title level={4}>{children}</Title>,
  p: ({ children }) => <Typography.Paragraph>{children}</Typography.Paragraph>,
  ul: props => <List>{props.children}</List>,
  li: props => <List.Item>{props.children}</List.Item>,
};

export default function QuestionDrawer({
  isDrawerVisible,
  handleClose,
}: QuestionDrawerProps) {
  const history = useHistory();
  const { t, i18n } = useTranslation(['common']);
  const temperedPath = useCallback(() => {
    return history.location.pathname.slice(1);
  }, [history.location.pathname]);

  const [data, setData] = useState<any>({});

  const fetchQuestions = useCallback(async () => {
    let response = await Fetch.get(
      `/api/wiki/?path=${temperedPath()}&language=${i18n.language}`
    );
    response = await response.json();
    setData(response);
  }, [temperedPath, i18n.language]);

  useEffect(() => {
    fetchQuestions();
  }, [history.location.pathname, fetchQuestions]);

  return (
    <Drawer
      className="[&_.ant-drawer-body]:p-0 [&_.markdown]:w-full [&_.markdown_img]:w-full"
      title={t('Help Center')}
      placement="right"
      closable
      onClose={handleClose}
      open={isDrawerVisible}
      width="40%"
    >
      {Object.keys(data).length !== 0 ? (
        data?.content.map((content: any, index: number) => (
          <Collapse key={`collapse-${index}`}>
            <Panel className="w-full" header={content.title} key={index}>
              <ReactMarkdown
                className="markdown"
                components={components}
                remarkPlugins={[gfm]}
                children={content.markdown}
              />
            </Panel>
          </Collapse>
        ))
      ) : (
        <StyledNoInfoMessage>
          {t('There is no information regarding this page')}
        </StyledNoInfoMessage>
      )}
    </Drawer>
  );
}
